import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  api,
  setAccessToken,
  getAccessToken,
  setSessionExpiredHandler,
  formatApiError,
  isDefinitiveRefreshFailure,
} from "@/lib/api";

const AuthContext = createContext(null);

// Promise partilhada a NÍVEL DE MÓDULO — sobrevive ao unmount/remount e ao
// duplo-invoke do React.StrictMode, garantindo UMA única chamada concorrente a
// POST /auth/refresh durante o bootstrap. Execuções concorrentes partilham a
// mesma promise e aguardam o mesmo resultado. Resolve com os dados do
// utilizador (de /auth/me) em caso de sucesso; rejeita se a sessão for
// realmente inválida (token expirado/ausente → 401).
let bootstrapRefreshPromise = null;

// Sem rede ou servidor indisponível no arranque, a sessão não está perdida:
// o cookie de refresh continua lá. Espera-se e volta-se a tentar.
const BOOTSTRAP_RETRY_MS = [2000, 5000, 10000, 20000, 30000];

export const RECONNECTING_MESSAGE = "Sem ligação ao servidor. A tentar novamente…";

function runBootstrapRefreshOnce() {
  if (!bootstrapRefreshPromise) {
    bootstrapRefreshPromise = (async () => {
      const res = await api.post("/auth/refresh", null);
      setAccessToken(res.data.access_token);
      const meRes = await api.get("/auth/me");
      return meRes.data;
    })().finally(() => {
      // Limpar em finally permite refreshes futuros (ex.: após logout/login),
      // sem reutilizar o refresh token (a rotação continua a cargo do backend).
      bootstrapRefreshPromise = null;
    });
  }
  return bootstrapRefreshPromise;
}

export function AuthProvider({ children }) {
  // status: 'loading' | 'anon' | 'authed'
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const queryClient = useQueryClient();

  // AUD-002/AUD-006 — única operação lógica de "terminar sessão localmente":
  // cache autenticada (React Query) + token + user + status, todos limpos
  // juntos. Usada tanto pelo logout explícito do utilizador como pela
  // notificação de sessão expirada (refresh definitivamente falhado a meio
  // de sessão) — nenhum dos dois caminhos duplica esta lógica.
  const clearSession = useCallback(async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    setAccessToken(null);
    setUser(null);
    setStatus("anon");
  }, [queryClient]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearSession();
    });
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  const bootstrap = useCallback(async () => {
    // Mantém status="loading" (estado inicial) enquanto a única tentativa
    // partilhada de refresh está em curso. Só passa a "anon" quando o
    // servidor recusar a sessão (401/403); outras falhas são transitórias. Devolve uma promise para o
    // useEffect poder ignorar o resultado após unmount (StrictMode).
    try {
      const u = await runBootstrapRefreshOnce();
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, transient: !isDefinitiveRefreshFailure(e) };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let attempt = 0;
    const run = () => {
      bootstrap().then((r) => {
        // Ignora o resultado da 1ª execução do StrictMode (cleanup marcou
        // cancelled=true); a 2ª execução (que partilha a MESMA promise) aplica
        // o estado. Preserva a rota originalmente pedida — não há navegação aqui.
        if (cancelled) return;
        if (r.ok) {
          setUser(r.user);
          setReconnecting(false);
          setStatus("authed");
        } else if (r.transient) {
          setReconnecting(true);
          const delay = BOOTSTRAP_RETRY_MS[Math.min(attempt, BOOTSTRAP_RETRY_MS.length - 1)];
          attempt += 1;
          timer = setTimeout(() => {
            timer = null;
            run();
          }, delay);
        } else {
          setAccessToken(null);
          setUser(null);
          setReconnecting(false);
          setStatus("anon");
        }
      });
    };
    const onOnline = () => {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      run();
    };
    window.addEventListener("online", onOnline);
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", onOnline);
    };
  }, [bootstrap]);

  const login = useCallback(async (username, password) => {
    const res = await api.post("/auth/login", { username, password });
    setAccessToken(res.data.access_token);
    setUser(res.data.user);
    setStatus("authed");
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: dissociate this device's push endpoint from the account
    // BEFORE clearing the token, so the device stops receiving push for the
    // previous user. This does not call subscription.unsubscribe() — the SW
    // subscription remains on the browser and can be re-associated on next
    // login. See TaskFlow Bloco 4 rules.
    try {
      if (getAccessToken() && "serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub && sub.endpoint) {
          await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        }
      }
    } catch (e) {
      /* best-effort — never block logout */
    }
    try {
      if (getAccessToken()) await api.post("/auth/logout", null);
    } catch (e) {
      /* ignore */
    }
    await clearSession();
  }, [clearSession]);

  const refreshMe = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  const value = useMemo(
    () => ({ status, user, reconnecting, login, logout, refreshMe, formatApiError }),
    [status, user, reconnecting, login, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
